/**
 * Timeframe menu with favourites inline and the full ladder behind a dropdown.
 *
 * Availability comes from the engine rather than being assumed: second-level
 * bars need a Polygon key, and an interval nobody can source is shown disabled
 * with the reason on hover instead of silently returning an empty chart.
 */
import { TIMEFRAME_GROUPS, TIMEFRAME_LABEL, type Timeframe } from "@tt/shared";
import { motion } from "motion/react";
import { useRef, useState } from "react";

import { Popover } from "./Popover";

export interface TimeframeAvailability {
  id: string;
  available: boolean;
  reason?: string;
  derived?: boolean;
  fetched_as?: string;
}

const QUICK: Timeframe[] = ["1m", "5m", "15m", "1h", "4h", "1d"];

/** Stiff and well damped: this should feel like the control settling, not
 *  like something bouncing. Anything slower than ~180ms reads as lag on a
 *  click the user expects to be instant. */
const SPRING = { type: "spring", stiffness: 520, damping: 38, mass: 0.7 } as const;

export function TimeframePicker({
  value, onChange, availability,
}: {
  value: string;
  onChange: (tf: Timeframe) => void;
  availability: Map<string, TimeframeAvailability>;
}) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const info = (tf: string) => availability.get(tf);
  const title = (tf: string) => {
    const a = info(tf);
    if (a && !a.available) return a.reason ?? "not available from the configured providers";
    if (a?.derived) return `${TIMEFRAME_LABEL[tf]} — aggregated from ${a.fetched_as} bars`;
    return TIMEFRAME_LABEL[tf] ?? tf;
  };

  // The current selection is pinned into the quick row so it is always visible
  // even when it is an unusual interval like 45m.
  const quick = QUICK.includes(value as Timeframe)
    ? QUICK
    : [...QUICK.slice(0, 5), value as Timeframe];

  return (
    <div className="tf-picker" ref={boxRef}>
      {/* One segmented control rather than seven boxes. The selection is a
          single element that slides between segments via a shared layoutId,
          so the eye tracks one object moving instead of two states blinking
          -- which is the whole reason to reach for a layout animation here
          rather than a CSS class swap. */}
      <div className="seg">
        {quick.map((tf) => {
          const a = info(tf);
          const disabled = a ? !a.available : false;
          const active = value === tf;
          return (
            <button
              key={tf}
              className={`seg-item ${active ? "active" : ""}`}
              disabled={disabled}
              title={title(tf)}
              onClick={() => onChange(tf)}
            >
              {active && (
                <motion.span
                  layoutId="tf-pill"
                  className="seg-pill"
                  transition={SPRING}
                />
              )}
              <span className="seg-label">{tf}</span>
            </button>
          );
        })}
        <button
          className={`seg-item seg-more ${open ? "on" : ""}`}
          onClick={() => setOpen(!open)}
          title="All timeframes"
          aria-label="All timeframes"
        >
          <span className="seg-label">⋯</span>
        </button>
      </div>

      <Popover anchorRef={boxRef} open={open} onClose={() => setOpen(false)} width={272}>
        <div className="tf-menu">
          {TIMEFRAME_GROUPS.map((g) => (
            <div key={g.group}>
              <div className="tf-menu-group">{g.group}</div>
              <div className="tf-menu-items">
                {g.items.map((tf) => {
                  const a = info(tf);
                  const disabled = a ? !a.available : false;
                  return (
                    <button
                      key={tf}
                      className={`btn small ${value === tf ? "active" : ""}`}
                      disabled={disabled}
                      title={title(tf)}
                      onClick={() => {
                        onChange(tf);
                        setOpen(false);
                      }}
                    >
                      {tf}
                      {a?.derived && <span className="tf-derived">•</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="tf-menu-note">
            <span className="tf-derived">•</span> aggregated from a finer interval.
            Disabled intervals need a data provider that publishes them.
          </div>
        </div>
      </Popover>
    </div>
  );
}
